import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, Trash2, Camera, X, Circle, CircleDot, CircleCheck } from "lucide-react";

const STATUS_TONE = {
  todo: "bg-anvi-100 text-anvi-700",
  in_progress: "bg-amber-50 text-amber-800",
  blocked: "bg-red-50 text-red-700",
  done: "bg-emerald-50 text-emerald-700",
};
const STATUS_LABEL = { todo: "To do", in_progress: "In progress", blocked: "Blocked", done: "Done" };
const STATUS_CYCLE = { todo: "in_progress", in_progress: "done", done: "todo", blocked: "in_progress" };

function MilestoneRow({ m, onToggle }) {
  const Icon = m.status === "done" ? CircleCheck : m.status === "in_progress" ? CircleDot : Circle;
  const iconTone = m.status === "done" ? "text-emerald-700" : m.status === "in_progress" ? "text-anvi-terracotta" : "text-anvi-400";
  const next = m.status === "done" ? "pending" : m.status === "in_progress" ? "done" : "in_progress";
  return (
    <button onClick={() => onToggle(m.id, next)} data-testid={`milestone-${m.id}`}
            className="w-full flex items-center gap-3 p-3 hover:bg-anvi-50 rounded-md text-left transition-colors">
      <Icon size={20} strokeWidth={1.5} className={iconTone} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${m.status === "done" ? "text-anvi-500 line-through" : "text-anvi-900"}`}>{m.name}</div>
        {m.completed_at && <div className="text-xs text-anvi-500">Done on {new Date(m.completed_at).toLocaleDateString("en-IN")}</div>}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-anvi-400">{m.status.replace("_", " ")}</div>
    </button>
  );
}

function PhotoStrip({ taskId, photos, onUpload, onOpen }) {
  const beforeRef = useRef();
  const duringRef = useRef();
  const afterRef = useRef();
  const grouped = { before: [], during: [], after: [] };
  photos.forEach((p) => grouped[p.kind]?.push(p));

  const trigger = (kind) => ({ before: beforeRef, during: duringRef, after: afterRef }[kind].current.click());

  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await onUpload(taskId, file, kind);
      toast.success(`${kind[0].toUpperCase() + kind.slice(1)} photo uploaded`);
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  return (
    <div className="mt-3 grid grid-cols-3 gap-2">
      {["before", "during", "after"].map((kind) => (
        <div key={kind} className="border border-anvi-200 rounded-md p-2" data-testid={`photo-slot-${kind}-${taskId}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">{kind}</span>
            <button onClick={() => trigger(kind)} data-testid={`upload-photo-${kind}-${taskId}`}
                    className="text-anvi-terracotta hover:text-anvi-terracotta-dark text-xs flex items-center gap-1">
              <Camera size={12} strokeWidth={1.5} /> Add
            </button>
          </div>
          <div className="flex gap-1.5 flex-wrap min-h-[56px]">
            {grouped[kind].length === 0 ? (
              <div className="text-xs text-anvi-400 flex items-center justify-center w-full py-3 border border-dashed border-anvi-200 rounded-sm">No {kind} photos</div>
            ) : grouped[kind].map((p) => (
              <img key={p.id} src={api.photoUrl(p.id)} alt={p.kind} onClick={() => onOpen(p)}
                   data-testid={`photo-${p.id}`}
                   className="w-14 h-14 object-cover rounded-sm border border-anvi-200 cursor-pointer hover:opacity-80" />
            ))}
          </div>
          <input ref={{ before: beforeRef, during: duringRef, after: afterRef }[kind]} type="file"
                 accept="image/*" className="hidden" onChange={upload(kind)} />
        </div>
      ))}
    </div>
  );
}

function TaskCard({ task, onStatusToggle, onDelete, onUpload }) {
  const [expanded, setExpanded] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  const loadPhotos = async () => {
    setLoadingPhotos(true);
    try { const r = await api.taskPhotos(task.id); setPhotos(r.data); }
    finally { setLoadingPhotos(false); }
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadPhotos();
  };

  const handleUpload = async (taskId, file, kind) => {
    await onUpload(taskId, file, kind);
    await loadPhotos();
  };

  return (
    <div className="bg-white border border-anvi-200 rounded-md p-4" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => onStatusToggle(task.id, STATUS_CYCLE[task.status])} data-testid={`task-status-toggle-${task.id}`}
                className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${task.status === "done" ? "bg-emerald-600 border-emerald-600" : "border-anvi-300 hover:border-anvi-terracotta"}`}>
          {task.status === "done" && <Check size={12} className="text-white" strokeWidth={3} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${task.status === "done" ? "text-anvi-500 line-through" : "text-anvi-900"}`}>{task.title}</div>
          {task.description && <div className="text-xs text-anvi-500 mt-0.5 truncate">{task.description}</div>}
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[task.status]}`}>{STATUS_LABEL[task.status]}</span>
            <button onClick={toggle} data-testid={`task-expand-${task.id}`}
                    className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 hover:text-anvi-terracotta">
              {task.photo_count || 0} photos · {expanded ? "hide" : "manage"}
            </button>
          </div>
        </div>
        <button onClick={() => onDelete(task.id)} data-testid={`task-delete-${task.id}`}
                className="shrink-0 text-anvi-400 hover:text-red-600 p-1">
          <Trash2 size={14} strokeWidth={1.5} />
        </button>
      </div>

      {expanded && (
        loadingPhotos
          ? <div className="text-xs text-anvi-500 mt-3">Loading photos…</div>
          : <PhotoStrip taskId={task.id} photos={photos} onUpload={handleUpload} onOpen={setPreview} />
      )}

      {preview && (
        <div className="fixed inset-0 bg-anvi-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-8" onClick={() => setPreview(null)}>
          <button className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-md"><X size={20} /></button>
          <img src={api.photoUrl(preview.id)} alt="preview" className="max-w-full max-h-full object-contain rounded-md" />
        </div>
      )}
    </div>
  );
}

export default function ExecutionPanel({ project }) {
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const refresh = async () => {
    const r = await api.execution(project.id);
    setMilestones(r.data.milestones);
    setTasks(r.data.tasks);
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [project.id]);

  const toggleMilestone = async (id, status) => {
    try { await api.milestoneUpdate(id, status); refresh(); toast.success("Milestone updated"); }
    catch { toast.error("Update failed"); }
  };
  const toggleTaskStatus = async (id, status) => {
    try { await api.taskUpdate(id, { status }); refresh(); }
    catch { toast.error("Update failed"); }
  };
  const deleteTask = async (id) => {
    try { await api.taskDelete(id); refresh(); toast.success("Task deleted"); }
    catch { toast.error("Delete failed"); }
  };
  const uploadPhoto = async (taskId, file, kind) => {
    await api.taskPhotoUpload(taskId, file, kind);
    refresh();
  };
  const submitNew = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await api.taskCreate(project.id, { title: newTitle });
      setNewTitle("");
      refresh();
      toast.success("Task added");
    } catch { toast.error("Could not add task"); }
    finally { setCreating(false); }
  };

  const doneCount = milestones.filter((m) => m.status === "done").length;
  const pct = milestones.length ? Math.round((doneCount / milestones.length) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="execution-panel">
      <div className="lg:col-span-1">
        <div className="bg-white border border-anvi-200 rounded-md p-5 sticky top-6">
          <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Milestones</div>
          <div className="font-heading text-xl text-anvi-900 mt-1">{doneCount} of {milestones.length}</div>
          <div className="h-1.5 bg-anvi-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-anvi-terracotta transition-all" style={{ width: `${pct}%` }} data-testid="milestones-progress" />
          </div>
          <div className="mt-3 space-y-0.5">
            {milestones.map((m) => <MilestoneRow key={m.id} m={m} onToggle={toggleMilestone} />)}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <form onSubmit={submitNew} className="bg-white border border-anvi-200 rounded-md p-4 flex gap-3 items-center">
          <Plus size={16} strokeWidth={1.5} className="text-anvi-500" />
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="new-task-title"
                 placeholder="Add a site task (e.g. Install TV unit veneer)…"
                 className="flex-1 bg-transparent focus:outline-none text-sm placeholder-anvi-400" />
          <button type="submit" disabled={creating || !newTitle.trim()} data-testid="add-task-btn"
                  className="text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark px-3 py-1.5 rounded-md transition-colors disabled:opacity-60">
            {creating ? "Adding…" : "Add"}
          </button>
        </form>

        {tasks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-anvi-200 rounded-md text-anvi-500 text-sm">
            No site tasks yet · start by adding one above
          </div>
        ) : (
          <div className="space-y-3" data-testid="tasks-list">
            {tasks.map((t) => (
              <TaskCard key={t.id} task={t} onStatusToggle={toggleTaskStatus} onDelete={deleteTask} onUpload={uploadPhoto} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
